import React, { createElement, useState } from "react"

import { ClickAwayListener, Popper, styled } from "@mui/material"
import T from "prop-types"

import { NestedMenu, NestedRoot, StyledMenuItem } from "./components"
import { MuiIcon } from "../../atoms"
import { Button } from "../../molecules"

const buttonHeight = 45

const StyledWrapper = styled("span")({
  "& > .Mui-disabled": {
    background: "#F1F3F4 !important",
    borderColor: "#F1F3F4 !important",
    color: "#757575 !important",
  },
  "&.toolbar-mode": {
    "& > .Mui-disabled": {
      borderBottom: "1px solid rgba(0, 0, 0, 0.42) !important",
    },
    "& .MuiButtonBase-root": {
      border: "none",
      fontWeight: 400,
      padding: "10px 12px",
      lineHeight: "20px",
      borderRadius: "4px 4px 0 0",
      borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
      "&:hover, &:focus": {
        backgroundColor: "rgb(241, 243, 244)",
        borderColor: "rgb(241, 243, 244)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
      },
      "& span:first-of-type": {
        width: "148px",
        textAlign: "left"
      },
      "& .MuiButton-endIcon": {
        marginRight: 0,
        marginLeft: "8px"
      }
    },
  },

  height: "100%",
})

export const PopupMenu = ({
  toolbarMode = false,
  buttonElement = Button,
  buttonProps,
  disabled,
  list,
  menuGap = 0,
  title,
  withoutArrow = false,
  maxHeight,
  dataQa
}) => {
  const [anchorElement, setAnchorElement] = useState(null)

  const handleClick = (event) => {
    setAnchorElement(anchorElement ? null : event.currentTarget)
  }

  const handleClose = () => {
    setAnchorElement(null)
  }

  const open = Boolean(anchorElement)

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <StyledWrapper className={toolbarMode ? "toolbar-mode" : ""}>
        {createElement(buttonElement, {
          disabled,
          endIcon: withoutArrow ? null : <MuiIcon.ArrowDropDown />,
          height: 44,
          label: title,
          disableRipple: toolbarMode,
          onClick: handleClick,
          variant: "contained",
          id: dataQa,
          ...buttonProps,
        })}

        {open && (
          <Popper
            anchorEl={anchorElement}
            open={open}
            style={{ zIndex: 100 }}
            placement="bottom-start">
            <NestedMenu open={open} maxHeight={maxHeight} menuGap={menuGap - buttonHeight}>
              {menuItemsFor(list, handleClose, toolbarMode)}
            </NestedMenu>
          </Popper>
        )}
      </StyledWrapper>
    </ClickAwayListener>
  )
}

const menuItemsFor = (list, handleClose, toolbarMode) => {
  return list.map(({ nestedMenuItems, onClick, title, disabled, id }) => {
    const handleItemClick = (event) => {
      event.stopPropagation()
      if (!onClick) return
      onClick()
      handleClose()
    }

    if (nestedMenuItems) {
      return (
        <NestedRoot
          key={title}
          title={title}
          id={id}
          handleClick={handleItemClick}
          toolbarMode={toolbarMode}>
          {menuItemsFor(nestedMenuItems, handleClose, toolbarMode)}
        </NestedRoot>
      )
    }

    return (
      <StyledMenuItem
        className={toolbarMode ? "toolbar-mode" : ""}
        key={title}
        disabled={disabled}
        id={id}
        onClick={handleItemClick}>
        {title}
      </StyledMenuItem>
    )
  })
}

PopupMenu.propTypes = {
  buttonElement: T.elementType,
  buttonProps: T.shape(Button.propTypes),
  disabled: T.bool,
  keepMounted: T.bool,
  list: T.arrayOf(T.shape({ disabled: T.bool, onClick: T.func, title: T.string.isRequired })),
  menuGap: T.oneOfType([T.number, T.string]),
  title: T.string,
  withoutArrow: T.bool,
}
